import type { Group, Lead, StageEvent } from './types';
import { offsetMs } from './businessHours';
import { isPaidSource } from './sources';

const HOUR = 3600_000;
const DAY = 24 * HOUR;
const TZ = 'Asia/Jerusalem';

const arrival = (l: Lead) => (l.leadDate ? Date.parse(l.leadDate) : NaN);
const within = (l: Lead, from: number, to: number) => { const t = arrival(l); return t >= from && t < to; };

export const NO_SOURCE = 'ללא מקור';
export const NO_MEDIUM = 'ללא ערוץ';
export const OTHER_SOURCES = 'אחר';

// ---- the last 7 days against the 7 before them -------------------------------------------------------------
export type SourceRow = { source: string; current: number; previous: number };
export type WeekCompare = {
  current: number; previous: number;
  /** Leads from paid sources ("… (paid)") in each period; the rest are organic, direct or unknown. */
  paid: { current: number; previous: number };
  sources: SourceRow[];
  /** By how the lead reached the business (Landing Page, Lead Form, WhatsApp). */
  mediums: SourceRow[];
};

/** Rows per key, biggest first; keys beyond the `top` biggest (both periods together) fold into "אחר". */
function breakdown(cur: Lead[], prev: Lead[], key: (l: Lead) => string, top: number): SourceRow[] {
  const rows = new Map<string, SourceRow>();
  const add = (l: Lead, period: 'current' | 'previous') => {
    const k = key(l);
    const r = rows.get(k) ?? { source: k, current: 0, previous: 0 };
    r[period]++;
    rows.set(k, r);
  };
  cur.forEach((l) => add(l, 'current'));
  prev.forEach((l) => add(l, 'previous'));
  const sorted = [...rows.values()].sort((a, b) => b.current + b.previous - (a.current + a.previous) || a.source.localeCompare(b.source));
  const head = sorted.slice(0, top);
  const tail = sorted.slice(top);
  if (tail.length) head.push({ source: OTHER_SOURCES, current: tail.reduce((n, r) => n + r.current, 0), previous: tail.reduce((n, r) => n + r.previous, 0) });
  return head;
}

/**
 * Leads that arrived in the last 7 × 24 hours against the 7 × 24 hours before that, overall, paid, per source and per
 * medium. Abandoned opportunities (duplicates closed by the CRM) are not leads and are left out.
 */
export function compareWeeks(leads: Lead[], now: number, top = 8): WeekCompare {
  const real = leads.filter((l) => l.status !== 'abandoned');
  const cur = real.filter((l) => within(l, now - 7 * DAY, now));
  const prev = real.filter((l) => within(l, now - 14 * DAY, now - 7 * DAY));
  return {
    current: cur.length, previous: prev.length,
    paid: { current: cur.filter((l) => isPaidSource(l.source)).length, previous: prev.filter((l) => isPaidSource(l.source)).length },
    sources: breakdown(cur, prev, (l) => l.source?.trim() || NO_SOURCE, top),
    mediums: breakdown(cur, prev, (l) => l.medium?.trim() || NO_MEDIUM, top),
  };
}

/** Change as a percentage of the earlier value; null when there is nothing to compare with. */
export function changePct(current: number, previous: number): number | null {
  return previous ? Math.round(((current - previous) / previous) * 100) : null;
}

// ---- the last 24 hours against the same 24 hours a week ago, in 2-hour buckets -------------------------------
export type HourBucket = { label: string; current: number; previous: number };
export type DayCompare = { current: number; previous: number; buckets: HourBucket[] };

/** Israel clock hour of an instant. */
function localHour(t: number): number {
  return new Date(t + offsetMs(t, TZ)).getUTCHours();
}

/**
 * Leads of the last 24 hours against the 24 hours that ended exactly a week ago (same weekday, same hours), counted
 * by the Israel clock hour they arrived in: 00–02, 02–04, … 22–24.
 */
export function compareDays(leads: Lead[], now: number): DayCompare {
  const buckets: HourBucket[] = Array.from({ length: 12 }, (_, i) => ({
    label: `${String(i * 2).padStart(2, '0')}–${String(i * 2 + 2).padStart(2, '0')}`, current: 0, previous: 0,
  }));
  let current = 0, previous = 0;
  for (const l of leads) {
    const t = arrival(l);
    if (t >= now - DAY && t < now) { buckets[Math.floor(localHour(t) / 2)].current++; current++; }
    else if (t >= now - 8 * DAY && t < now - 7 * DAY) { buckets[Math.floor(localHour(t) / 2)].previous++; previous++; }
  }
  return { current, previous, buckets };
}

// ---- funnel -------------------------------------------------------------------------------------------------
export const FUNNEL = {
  noAnswer: 'ללא מענה',     // status
  irrelevant: 'לא רלוונטי', // status
  won: 'נסגר',              // status
  car: 'למצוא רכב ספציפי',  // group
  funding: 'בדיקת מימון',   // group
} as const;

export type Funnel = {
  incoming: number;
  /** Reached at some point (a lead can be in more than one): no answer, irrelevant straight from triage, specific car, funding. */
  noAnswer: number; irrelevant: number; car: number; funding: number;
  /** Outcomes of the leads that reached a sales step (car or funding): won, lost (irrelevant after sales), still open. */
  sales: number; won: number; lost: number; open: number;
  /** Leads whose whole path is known (they arrived after recording began); the rest count by what is known. */
  withPath: number;
};

/**
 * Where the leads that arrived in a period went. A lead's path is its recorded status changes plus its current status;
 * for a lead that arrived before recording began, only the part since then (often just its current status) is known.
 */
export function funnel(o: { leads: Lead[]; groups: Group[]; stageName: (id: string) => string; events: StageEvent[]; trackingSince: string | null }): Funnel {
  const byOpp = new Map<string, StageEvent[]>();
  for (const e of o.events) { const a = byOpp.get(e.opportunityId); if (a) a.push(e); else byOpp.set(e.opportunityId, [e]); }
  const groupStages = (name: string) => new Set(o.groups.find((g) => g.name === name)?.stageNames ?? [name]);
  const carStages = groupStages(FUNNEL.car);
  const fundingStages = groupStages(FUNNEL.funding);
  const since = o.trackingSince ? Date.parse(o.trackingSince) : Infinity;
  const f: Funnel = { incoming: 0, noAnswer: 0, irrelevant: 0, car: 0, funding: 0, sales: 0, won: 0, lost: 0, open: 0, withPath: 0 };

  for (const l of o.leads) {
    f.incoming++;
    if (arrival(l) >= since) f.withPath++;
    const evs = (byOpp.get(l.opportunityId) ?? []).slice().sort((a, b) => a.at.localeCompare(b.at));
    const path = (evs.length ? [evs[0].from, ...evs.map((e) => e.to)] : []).map(o.stageName);
    const current = o.stageName(l.stageId);
    if (path[path.length - 1] !== current) path.push(current);

    const firstSales = path.findIndex((s) => carStages.has(s) || fundingStages.has(s));
    const irrelevantAt = path.indexOf(FUNNEL.irrelevant);
    if (path.includes(FUNNEL.noAnswer)) f.noAnswer++;
    if (path.some((s) => carStages.has(s))) f.car++;
    if (path.some((s) => fundingStages.has(s))) f.funding++;
    if (irrelevantAt >= 0 && (firstSales < 0 || irrelevantAt < firstSales)) f.irrelevant++;
    // a lead closed without a recorded sales step (e.g. before recording began) still counts as a sale that was won
    const won = path.includes(FUNNEL.won);
    if (firstSales >= 0 || won) {
      f.sales++;
      if (won) f.won++;
      else if (path.slice(firstSales).includes(FUNNEL.irrelevant)) f.lost++;
      else f.open++;
    }
  }
  return f;
}

// ---- what happened in a period ----------------------------------------------------------------------------------
export type Activity = {
  /** Leads that arrived in the period. */
  arrived: number;
  /** Distinct leads that moved, within the period, into: */
  noAnswer: number; irrelevant: number; car: number; funding: number; won: number; lost: number;
};

/**
 * The period's events rather than its leads: how many leads moved into each step during [from, to), whenever they
 * arrived. Built from recorded status changes only, so it covers the time since recording began. "Irrelevant" and
 * "lost" split moves into לא רלוונטי by whether the lead had passed a sales step (car / funding) before it.
 */
export function activity(o: { leads: Lead[]; groups: Group[]; stageName: (id: string) => string; events: StageEvent[]; from: number; to: number }): Activity {
  const groupStages = (name: string) => new Set(o.groups.find((g) => g.name === name)?.stageNames ?? [name]);
  const carStages = groupStages(FUNNEL.car);
  const fundingStages = groupStages(FUNNEL.funding);
  const sets = { noAnswer: new Set<string>(), irrelevant: new Set<string>(), car: new Set<string>(), funding: new Set<string>(), won: new Set<string>(), lost: new Set<string>() };
  const byOpp = new Map<string, StageEvent[]>();
  for (const e of o.events) { const a = byOpp.get(e.opportunityId); if (a) a.push(e); else byOpp.set(e.opportunityId, [e]); }
  for (const [opp, evs] of byOpp) {
    evs.sort((a, b) => a.at.localeCompare(b.at));
    let sales = false;
    for (const e of evs) {
      const from = o.stageName(e.from), to = o.stageName(e.to);
      if (carStages.has(from) || fundingStages.has(from)) sales = true;
      const t = Date.parse(e.at);
      if (t >= o.from && t < o.to) {
        if (to === FUNNEL.noAnswer) sets.noAnswer.add(opp);
        if (carStages.has(to) && !carStages.has(from)) sets.car.add(opp);
        if (fundingStages.has(to) && !fundingStages.has(from)) sets.funding.add(opp);
        if (to === FUNNEL.won) sets.won.add(opp);
        if (to === FUNNEL.irrelevant) (sales ? sets.lost : sets.irrelevant).add(opp);
      }
      if (carStages.has(to) || fundingStages.has(to)) sales = true;
    }
  }
  return {
    arrived: o.leads.filter((l) => within(l, o.from, o.to)).length,
    noAnswer: sets.noAnswer.size, irrelevant: sets.irrelevant.size, car: sets.car.size, funding: sets.funding.size, won: sets.won.size, lost: sets.lost.size,
  };
}
